const libPictView = require('pict-view');

/**
 * @typedef {import('../providers/ServiceCatalog.js').ServiceType} ServiceType
 */

/**
 * @typedef {Object} CellDisplayInfo
 * @property {string} EnvLabel
 * @property {string} EnvKey
 * @property {string} Version
 * @property {string} RepoLink
 * @property {boolean} HasVersion
 */

/**
 * @typedef {Object} RowDisplayInfo
 * @property {string} Name
 * @property {ServiceType} Type
 * @property {CellDisplayInfo[]} Cells
 * @property {boolean} HasDrift
 * @property {string} DriftMarker
 */

class MultiEnvVersionsView extends libPictView
{
    constructor(pFable, pOptions, pServiceHash)
    {
        super(pFable, pOptions, pServiceHash);

        /** @type {import('../Application-Web').INTERNALSPict} */
        this.pict;

        this.reset();
    }

    /**
     * @return {import('../providers/ServiceCatalog.js')} The shared service inventory.
     */
    get serviceCatalog()
    {
        return this.pict.providers.InternalsServiceCatalog;
    }

    reset()
    {
        if (!this.AppData.Display)
        {
            this.AppData.Display = {};
        }
        /** @type {RowDisplayInfo[]} */
        this.AppData.Display.MultiEnvGroups = [];
        this.AppData.Display.MultiEnvHeaders = this.serviceCatalog.environments.map((pEnvironment) => ({ Label: pEnvironment.Label, Key: pEnvironment.Key }));
        this.AppData.Display.MultiEnvColumnSpan = this.serviceCatalog.environments.length + 1;
    }

    /**
     * Fetch every environment for one catalog entry and shape it into a display row.
     *
     * @param {any} pService - The catalog entry.
     *
     * @return {Promise<RowDisplayInfo>} The row.
     */
    async buildRow(pService)
    {
        const tmpEnvironments = this.serviceCatalog.environments;
        const tmpVersions = await Promise.all(tmpEnvironments.map(
            (pEnvironment) => this.serviceCatalog.fetchVersionForService(pService, pEnvironment.Key)));
        const tmpCells = tmpEnvironments.map((pEnvironment, pIndex) => (
            {
                EnvLabel: pEnvironment.Label,
                EnvKey: pEnvironment.Key,
                Version: tmpVersions[pIndex] || '???',
                RepoLink: this.serviceCatalog.buildHeadCommitURL(pService, tmpVersions[pIndex]),
                HasVersion: this.serviceCatalog.isRealVersion(tmpVersions[pIndex]),
            }));
        const tmpHasDrift = new Set(tmpVersions.filter((pVersion) => this.serviceCatalog.isRealVersion(pVersion))).size > 1;
        return (
            {
                Name: pService.Name,
                Type: pService.Type,
                Cells: tmpCells,
                HasDrift: tmpHasDrift,
                DriftMarker: tmpHasDrift ? 'drift' : '',
            });
    }

    async buildModel()
    {
        const tmpRows = await Promise.all(this.serviceCatalog.services.map((pService) => this.buildRow(pService)));
        const tmpRowByService = new Map(this.serviceCatalog.services.map((pService, pIndex) => [ pService, tmpRows[pIndex] ]));
        this.AppData.Display.MultiEnvGroups = this.serviceCatalog.groups.map((pGroup) => (
            {
                Label: pGroup.Label,
                ColumnSpan: this.AppData.Display.MultiEnvColumnSpan,
                Rows: this.serviceCatalog.servicesInGroup(pGroup.Key).map((pService) => tmpRowByService.get(pService)),
            }));
    }

    async onBeforeRenderAsync(pCallback, pRenderable)
    {
        this.reset();
        await this.buildModel();
        super.onBeforeRenderAsync(pCallback, pRenderable);
    }
}

module.exports = MultiEnvVersionsView;
module.exports.default_configuration = (
    {
        ViewIdentifier: "ServiceVersionsMultiEnv",
        DefaultRenderable: "ServiceVersionsMultiEnv",
        DefaultDestinationAddress: "#PlatformConfiguration-Application-Container",

        AutoRender: true,

        CSS: /*css*/`
        .multi-env-versions-table {
            width: 100%;
            border-collapse: collapse;
        }
        .multi-env-versions-table th,
        .multi-env-versions-table td {
            padding: 0.5rem 0.75rem;
            border-bottom: 1px dotted var(--bulma-border);
            vertical-align: middle;
        }
        .multi-env-versions-table th {
            text-align: left;
            background-color: var(--bulma-background);
        }
        .multi-env-versions-table tr.has-version-drift {
            background-color: var(--bulma-warning-soft, rgba(255, 224, 138, 0.35));
        }
        .multi-env-versions-table tr.has-version-drift td:first-child {
            border-left: 3px solid var(--bulma-warning, #ffd57d);
        }
        .multi-env-versions-table .product-name {
            font-weight: 600;
        }
        .multi-env-versions-table tr.group-heading th {
            padding-top: 1.25rem;
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: var(--bulma-text-50);
            background-color: transparent;
        }
        .multi-env-versions-table .env-label {
            text-transform: uppercase;
            font-size: 0.85rem;
            letter-spacing: 0.04em;
        }
        `,

        Templates: [
            {
                Hash: "MultiEnvVersionsView-Wrapper",
                Template: /*html*/`
                <section id="MultiEnvVersionsView" class="card p-5">
                    <h2 class="title is-4 pb-4 mb-0 has-text-text-50">Versions Across Environments</h2>
                    <p class="pb-3">Highlighted rows have version drift across environments.</p>
                    <table class="multi-env-versions-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                {~TS:MultiEnvVersionsView-Header:AppData.Display.MultiEnvHeaders~}
                            </tr>
                        </thead>
                        {~TS:MultiEnvVersionsView-Group:AppData.Display.MultiEnvGroups~}
                    </table>
                </section>
                `
            },
            {
                Hash: "MultiEnvVersionsView-Group",
                Template: /*html*/`
                <tbody>
                    <tr class="group-heading">
                        <th colspan="{~D:Record.ColumnSpan~}">{~D:Record.Label~}</th>
                    </tr>
                    {~TS:MultiEnvVersionsView-Row:Record.Rows~}
                </tbody>
                `
            },
            {
                Hash: "MultiEnvVersionsView-Header",
                Template: /*html*/`
                <th class="env-label">{~D:Record.Label~}</th>
                `
            },
            {
                Hash: "MultiEnvVersionsView-Row",
                Template: /*html*/`
                <tr class="{~TIfAbs:MultiEnvVersionsView-DriftClass:Record:Record.DriftMarker^>^0~}">
                    <td class="product-name">{~D:Record.Name~}</td>
                    {~TS:MultiEnvVersionsView-Cell:Record.Cells~}
                </tr>
                `
            },
            {
                Hash: "MultiEnvVersionsView-DriftClass",
                Template: /*html*/`has-version-drift`
            },
            {
                Hash: "MultiEnvVersionsView-Cell",
                Template: /*html*/`
                <td>
                    <span class="tag">{~D:Record.Version~}</span>
                    {~TIfAbs:MultiEnvVersionsView-Cell-RepoLink:Record:Record.RepoLink^>^0~}
                </td>
                `
            },
            {
                Hash: "MultiEnvVersionsView-Cell-RepoLink",
                Template: /*html*/`
                <a href="{~D:Record.RepoLink~}" target="_blank" title="Click to see the code of this version" class="ml-1">
                    <i class="fa-regular fa-code-branch"></i>
                </a>
                `
            },
        ],
        Renderables: [
            {
                RenderableHash: "ServiceVersionsMultiEnv",
                TemplateHash: "MultiEnvVersionsView-Wrapper",
                RenderMethod: 'replace'
            }]
    });
